
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import Actions from '../../../../components/docs/Actions';
import AlertWarning from 'plaid-threads/Icons/AlertError';
import Auth from 'plaid-threads/Icons/ProductAuth';
import ContentSwitcher from '../../../../components/docs/ContentSwitcher';
import SampleCode from '../../../../components/docs/plaidoms/SampleCode';
import ProductTour from 'src/components/docs/ProductTour/ProductTour';
export const metadata = {
  toc: true,
  maxTocLevel: 4,
  layout: 'guide',
  description: 'Learn how to use automated micro-deposits to quickly and securely verify your users'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const MDXCodeBlock = makeShortcode("MDXCodeBlock");
const Callout = makeShortcode("Callout");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <Header title='Automated Micro-deposits' subtitle='Learn how to authenticate your users in a secure and frictionless micro-deposit flow' mdxType="Header" />
    <h4 {...{
      "id": "the-automated-micro-deposit-flow"
    }}>{`The Automated Micro-deposit flow`}</h4>
    <p>{`The Automated Micro-deposits authentication flow is supported for an additional ~1,750 financial institutions in the US where Instant Auth is not available. These institutions together account for approximately <1% of US depository accounts.
Plaid will make a single micro-deposit and then automatically verify it within one to two business days.`}</p>
    <ProductTour steps={[{
      text: 'The user launches Link...',
      image: '/assets/img/docs/auth/amd_tour/link-1-rm.png'
    }, {
      text: '...and selects the institution to link.',
      image: '/assets/img/docs/auth/amd_tour/select-institution.png'
    }, {
      text: 'They find their institution...',
      image: '/assets/img/docs/auth/amd_tour/fi-selected.png'
    }, {
      text: '...and log in.',
      image: '/assets/img/docs/auth/amd_tour/fi-login.png'
    }, {
      text: 'Next, they select the account to link. (Single-account select is typically recommended for Auth.)',
      image: '/assets/img/docs/auth/amd_tour/account-select.png'
    }, {
      text: "They verify their bank's routing and account numbers...",
      image: '/assets/img/docs/auth/amd_tour/verify-account-routing.png'
    }, {
      text: '...and their name.',
      image: '/assets/img/docs/auth/amd_tour/enter-name.png'
    }, {
      text: 'Plaid will send a micro-deposit. Once it lands, Plaid will automatically detect it and verify the account.',
      image: '/assets/img/docs/auth/amd_tour/await-verification.png'
    }]} mdxType="ProductTour" />
    <p>{`You can try out the Automated Micro-deposits flow in an `}<a parentName="p" {...{
        "href": "https://plaid.coastdemo.com/share/67d0ce0df465686c02cc4fd2?zoom=100&step=6"
      }}>{`Interactive Demo`}</a>{`. See more details in our `}<a parentName="p" {...{
        "href": "/docs/auth/coverage/testing/#testing-automated-micro-deposits"
      }}>{`testing guide`}</a>{`.`}</p>
    <p>{`A user connects their financial institution using the following connection flow:`}</p>
    <ol>
      <li parentName="ol">{`Starting on a page in your app, the user clicks an action that opens Plaid Link, with the correct
Auth `}<a parentName="li" {...{
          "href": "#configure--create-a-link_token"
        }}>{`configuration`}</a>{`.`}</li>
      <li parentName="ol">{`Inside of Plaid Link, the user selects their institution, authenticates with their credentials,
provides their account and routing number, and enters in their legal name.`}</li>
      <li parentName="ol">{`Upon `}<a parentName="li" {...{
          "href": "#exchange-the-public-token"
        }}>{`successful authentication`}</a>{`, Link closes with a
`}<inlineCode parentName="li">{`public_token`}</inlineCode>{` and a `}<inlineCode parentName="li">{`metadata`}</inlineCode>{` account status of `}<inlineCode parentName="li">{`pending_automatic_verification`}</inlineCode>{`.`}</li>
      <li parentName="ol">{`Behind the scenes, Plaid sends a single micro-deposit to the user's account and will automatically
verify the deposited amounts within one to two business days.`}</li>
      <li parentName="ol">{`When verification succeeds or fails, Plaid sends an `}<a parentName="li" {...{
          "href": "#handle-auth-webhooks"
        }}>{`Auth webhook`}</a>{`,
which you can use to notify the user that their account is ready to move money. Once this step is done, your user's Auth data is verified and `}<a parentName="li" {...{
          "href": "#fetch-auth-data"
        }}>{`ready to fetch`}</a>{`.`}</li>
    </ol>
    <h4 {...{
      "id": "configure--create-a-link_token"
    }}>{`Configure & Create a link_token`}</h4>
    <p>{`Create a `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` with the following parameters:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`products`}</inlineCode>{` array containing `}<inlineCode parentName="li">{`auth`}</inlineCode>{` or `}<inlineCode parentName="li">{`transfer`}</inlineCode>{` -- unlike with Same-Day Micro-deposits, you can also include other products besides `}<inlineCode parentName="li">{`auth`}</inlineCode>{` or `}<inlineCode parentName="li">{`transfer`}</inlineCode>{` when creating a Link token for use with Automated Micro-deposits, but `}<inlineCode parentName="li">{`auth`}</inlineCode>{` or `}<inlineCode parentName="li">{`transfer`}</inlineCode>{` must be present.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`country_codes`}</inlineCode>{` set to `}<inlineCode parentName="li">{`['US']`}</inlineCode>{` – Micro-deposit verification is currently only available in the United States.`}</li>
      <li parentName="ul">{`A `}<inlineCode parentName="li">{`webhook`}</inlineCode>{` URL to receive a POST HTTPS request sent from Plaid's servers to your application
server, after Automated Micro-deposits succeeds or fails verification of a user's micro-deposits.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`auth`}</inlineCode>{` object should specify `}<inlineCode parentName="li">{`"automated_microdeposits_enabled": true`}</inlineCode></li>
    </ul>
    <MDXCodeBlock type='server' title='Automated Micro-deposits Configuration' mdxType="MDXCodeBlock">
      <pre><code parentName="pre" {...{
          "className": "language-bash"
        }}>{`curl -X POST https://sandbox.plaid.com/link/token/create \\
-H 'Content-Type: application/json' \\
-d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "user": {"client_user_id": "\${UNIQUE_USER_ID}"},
  "client_name": "Plaid App",
  "products": ["auth"],
  "country_codes": ["US"],
  "language": "en",
  "webhook": "https://sample-web-hook.com",
  "auth": {
    "automated_microdeposits_enabled": true
  }
}'
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-ruby"
        }}>{`link_token_create_request = Plaid::LinkTokenCreateRequest.new(
  {
    user: {
      client_user_id: "user-id",
    },
    client_name: 'Plaid Test App',
    products: ['auth'],
    country_codes: ['US'],
    language: "en",
    webhook: 'https://webhook.sample.com',
    link_customization_name: "default",
    auth: {
        automated_microdeposits_enabled: true,
    },
  }
)
response = client.link_token_create(
  link_token_create_request
)
link_token = response.link_token
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-node"
        }}>{`const request: LinkTokenCreateRequest = {
  user: { client_user_id: new Date().getTime().toString() },
  client_name: 'Plaid App',
  products: [Products.Auth],
  country_codes: [CountryCode.Us],
  language: 'en',
  auth: {
    automated_microdeposits_enabled: true,
  },
};
try {
  const response = await plaidClient.linkTokenCreate(request);
  const linkToken = response.data.link_token;
} catch (error) {
  // handle error
}
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-java"
        }}>{`LinkTokenCreateRequestAuth auth = new LinkTokenCreateRequestAuth()
    .automatedMicrodepositsEnabled(true);

LinkTokenCreateRequest request = new LinkTokenCreateRequest()
    .user(user)
    .clientName("very nice client name")
    .products(Arrays.asList(Products.AUTH))
    .countryCodes(Arrays.asList(CountryCode.US))
    .language("en")
    .webhook("https://example.com/webhook")
    .linkCustomizationName("default")
    .accountFilters(accountFilters)
    .auth(auth);
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-python"
        }}>{`request = LinkTokenCreateRequest(
    products=[Products('auth')],
    client_name="Plaid Test App",
    country_codes=[CountryCode('US')],
    language='en',
    webhook='https://sample-webhook-uri.com',
    link_customization_name='default',
    user=LinkTokenCreateRequestUser(
        client_user_id='user-id',
    ),
    auth=LinkTokenCreateRequestAuth(
        automated_microdeposits_enabled=True,
    )
)

response = client.link_token_create(request)
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go"
        }}>{`user := plaid.LinkTokenCreateRequestUser{
  ClientUserId: "user-id",
}

request := plaid.NewLinkTokenCreateRequest(
  "Plaid Test",
  "en",
  []plaid.CountryCode{plaid.COUNTRYCODE_US},
)
request.SetUser(user)
request.SetProducts([]plaid.Products{plaid.PRODUCTS_AUTH})
request.SetLinkCustomizationName("default")
request.SetWebhook("https://webhook-uri.com")
var automatedMicrodepositsEnabled bool = true
request.SetAuth(plaid.LinkTokenCreateRequestAuth{
  AutomatedMicrodepositsEnabled: &automatedMicrodepositsEnabled,
})
resp, _, err := client.PlaidApi.LinkTokenCreate(ctx).LinkTokenCreateRequest(*request).Execute()
if err != nil {
  panic(err)
}

linkToken := resp.GetLinkToken()
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go",
          "metastring": "sdk=fern",
          "sdk": "fern"
        }}>{`user := plaid.LinkTokenCreateRequestUser{ClientUserID: "user-id"}

request := &plaid.LinkTokenCreateRequest{
    ClientName:   "Plaid Test",
    Language:     "en",
    CountryCodes: []plaid.CountryCode{plaid.CountryCodeUs},
}
request.User = &user
request.Products = []plaid.Products{plaid.ProductsAuth}
request.LinkCustomizationName = plaid.String("default")
request.Webhook = plaid.String("https://webhook-uri.com")
var automatedMicrodepositsEnabled bool = true
request.Auth = &plaid.LinkTokenCreateRequestAuth{AutomatedMicrodepositsEnabled: &automatedMicrodepositsEnabled}
resp, err := client.Link.CreateToken(ctx, request)
if err != nil {
    panic(err)
}

linkToken := resp.GetLinkToken()
`}</code></pre>
    </MDXCodeBlock>
    <h4 {...{
      "id": "initialize-link-with-a-link_token"
    }}>{`Initialize Link with a link_token`}</h4>
    <p>{`After creating a `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` for the `}<inlineCode parentName="p">{`auth`}</inlineCode>{` product, use it to initialize Plaid Link.`}</p>
    <p>{`When the user inputs their username and password, and account and routing numbers for the financial institution,
the `}<inlineCode parentName="p">{`onSuccess()`}</inlineCode>{` callback function will return a `}<inlineCode parentName="p">{`public_token`}</inlineCode>{`, with `}<inlineCode parentName="p">{`verification_status`}</inlineCode>{` equal to `}<inlineCode parentName="p">{`'pending_automatic_verification'`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "switcher=false #App.js",
        "switcher": "false",
        "#App.js": true
      }}>{`const linkHandler = Plaid.create({
  // Fetch a link_token configured for 'auth' from your app server
  token: (await $.post('/create_link_token')).link_token,
  onSuccess: (public_token, metadata) => {
    // Send the public_token and accounts to your app server
    $.post('/exchange_public_token', {
      publicToken: public_token,
      accounts: metadata.accounts,
    });

    metadata = {
      ...,
      link_session_id: String,
      institution: { name: 'State Employees Credit Union', institution_id: 'ins_100017' },
      accounts: [{
        id: 'vzeNDwK7KQIm4yEog683uElbp9GRLEFXGK98D',
        mask: '0000',
        name: null,
        type: 'depository',
        subtype: 'checking',
        verification_status: 'pending_automatic_verification'
      }]
    }
  },
  // ...
});

// Open Link on user-action
linkHandler.open();
`}</code></pre>
    <h5 {...{
      "id": "display-a-pending-status-in-your-app"
    }}>{`Display a "pending" status in your app`}</h5>
    <p>{`Because Automated verification usually takes one to two days to complete, we recommend displaying a UI
in your app that communicates to a user that verification will occur automatically and is currently pending.`}</p>
    <p>{`You can use the `}<inlineCode parentName="p">{`verification_status`}</inlineCode>{` key returned in the `}<inlineCode parentName="p">{`onSuccess`}</inlineCode>{` `}<inlineCode parentName="p">{`metadata.accounts`}</inlineCode>{` object once
Plaid Link closes successfully.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "switcher=false #Metadata verification_status",
        "switcher": "false",
        "#Metadata": true,
        "verification_status": true
      }}>{`verification_status: 'pending_automatic_verification';
`}</code></pre>
    <p>{`You can also `}<a parentName="p" {...{
        "href": "#check-the-account-verification-status-optional"
      }}>{`fetch the `}<inlineCode parentName="a">{`verification_status`}</inlineCode></a>{` for an
Item's account via the Plaid API to obtain the latest account status.`}</p>
    <h4 {...{
      "id": "exchange-the-public-token"
    }}>{`Exchange the public token`}</h4>
    <p>{`In your own backend server, call the `}<inlineCode parentName="p">{`/item/public_token/exchange`}</inlineCode>{`
endpoint with the Link `}<inlineCode parentName="p">{`public_token`}</inlineCode>{` received in the `}<inlineCode parentName="p">{`onSuccess`}</inlineCode>{` callback to obtain an `}<inlineCode parentName="p">{`access_token`}</inlineCode>{`.
Persist the returned `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` and `}<inlineCode parentName="p">{`item_id`}</inlineCode>{` in your database in relation to the user.`}</p>
    <p>{`Note that micro-deposits will only be delivered to the ACH network in the Production environment. To test your integration outside of Production, see `}<a parentName="p" {...{
        "href": "/docs/auth/coverage/testing#testing-automated-micro-deposits"
      }}>{`Testing automated micro-deposits in Sandbox`}</a>{`.`}</p>
    <MDXCodeBlock type='server' title='Exchange token request' mdxType="MDXCodeBlock">
      <pre><code parentName="pre" {...{
          "className": "language-bash"
        }}>{`curl -X POST https://sandbox.plaid.com/item/public_token/exchange \\
  -H 'Content-Type: application/json' \\
  -d '{
    "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
    "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
    "public_token": "public-sandbox-b0e2c4ee-a763-4df5-bfe9-46a46bce993d"
  }'
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-node"
        }}>{`// publicToken and accountID are sent from your app to your backend-server
const accountID = 'vzeNDwK7KQIm4yEog683uElbp9GRLEFXGK98D';
const publicToken = 'public-sandbox-b0e2c4ee-a763-4df5-bfe9-46a46bce993d';

// Obtain an access_token from the Link public_token
try {
  const response = await client.itemPublicTokenExchange({
    public_token: publicToken,
  });
  const accessToken = response.data.access_token;
} catch (err) {
  // handle error
}
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-python"
        }}>{`# publicToken and accountID are sent from your app to your backend-server
accountID = 'vzeNDwK7KQIm4yEog683uElbp9GRLEFXGK98D'
publicToken = 'public-sandbox-b0e2c4ee-a763-4df5-bfe9-46a46bce993d'

# Obtain an access_token from the Link public_token
token_request = ItemPublicTokenExchangeRequest(
      public_token=publicToken)
token_response = client.item_public_token_exchange(token_request)
accessToken = token_response['access_token']
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-ruby"
        }}>{`# publicToken and accountID are sent from your app to your backend-server
accountID = 'vzeNDwK7KQIm4yEog683uElbp9GRLEFXGK98D'
publicToken = 'public-sandbox-b0e2c4ee-a763-4df5-bfe9-46a46bce993d'

# Obtain an access_token from the Link public_token
item_public_token_exchange_request = Plaid::ItemPublicTokenExchangeRequest.new(
  {
    public_token: publicToken
  }
)
token_response = client.item_public_token_exchange(
  item_public_token_exchange_request
)
access_token = token_response.access_token
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-java"
        }}>{`// publicToken and accountID are sent from your app to your backend-server
String accountID = "vzeNDwK7KQIm4yEog683uElbp9GRLEFXGK98D";
String publicToken = "public-sandbox-b0e2c4ee-a763-4df5-bfe9-46a46bce993d";

// Obtain an access_token from the Link public_token
ItemPublicTokenExchangeRequest tokenRequest = new ItemPublicTokenExchangeRequest()
  .publicToken(publicToken);

Response<ItemPublicTokenExchangeResponse> tokenResponse = plaidClient
  .itemPublicTokenExchange(tokenRequest)
  .execute();
String accessToken = tokenResponse.body().getAccessToken();
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go"
        }}>{`// publicToken and accountID are sent from your app to your backend-server
accountID := "vzeNDwK7KQIm4yEog683uElbp9GRLEFXGK98D"
publicToken := "public-sandbox-b0e2c4ee-a763-4df5-bfe9-46a46bce993d"

// Obtain an access_token from the Link public_token
exchangePublicTokenResp, _, err := client.PlaidApi.ItemPublicTokenExchange(ctx).ItemPublicTokenExchangeRequest(
  *plaid.NewItemPublicTokenExchangeRequest(publicToken),
).Execute()

accessToken := exchangePublicTokenResp.GetAccessToken()
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go",
          "metastring": "sdk=fern",
          "sdk": "fern"
        }}>{`// publicToken and accountID are sent from your app to your backend-server
accountID := "vzeNDwK7KQIm4yEog683uElbp9GRLEFXGK98D"
publicToken := "public-sandbox-b0e2c4ee-a763-4df5-bfe9-46a46bce993d"

// Obtain an access_token from the Link public_token
exchangePublicTokenResp, err := client.Item.ExchangePublicToken(ctx, &plaid.ItemPublicTokenExchangeRequest{PublicToken: publicToken})

accessToken := exchangePublicTokenResp.GetAccessToken()
`}</code></pre>
    </MDXCodeBlock>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "switcher=false #Exchange token response",
        "switcher": "false",
        "#Exchange": true,
        "token": true,
        "response": true
      }}>{`{
  "access_token": "access-sandbox-5cd6e1b1-1b5b-459d-9284-366e2da89755",
  "item_id": "M5eVJqLnv3tbzdngLDp9FL5OlDNxlNhlE55op",
  "request_id": "m8MDnv9okwxFNBV"
}
`}</code></pre>
    <h4 {...{
      "id": "handle-auth-webhooks"
    }}>{`Handle Auth webhooks`}</h4>
    <p>{`Before you can call `}<inlineCode parentName="p">{`/auth/get`}</inlineCode>{` to fetch Auth data for a user's `}<inlineCode parentName="p">{`access_token`}</inlineCode>{`, a micro-deposit first
needs to post successfully to the user's bank account. Because Plaid uses Same Day ACH to send a
single micro-deposit amount, this process usually takes one to two days.`}</p>
    <p>{`Once the deposit has arrived in the user's account, Plaid will automatically verify the deposit
transaction and send an `}<a parentName="p" {...{
        "href": "/docs/api/products/auth/#automatically_verified"
      }}><inlineCode parentName="a">{`AUTOMATICALLY_VERIFIED`}</inlineCode></a>{`
webhook to confirm the account and routing numbers have been successfully verified.`}</p>
    <Callout warning mdxType="Callout">
      <p>{`Attempting to call `}<inlineCode parentName="p">{`/auth/get`}</inlineCode>{` on an unverified `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` will result
in a `}<a parentName="p" {...{
          "href": "/docs/errors/item#product_not_ready"
        }}><inlineCode parentName="a">{`PRODUCT_NOT_READY`}</inlineCode></a>{` error.`}</p>
    </Callout>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "#Auth AUTOMATICALLY_VERIFIED webhook",
        "#Auth": true,
        "AUTOMATICALLY_VERIFIED": true,
        "webhook": true
      }}>{`> POST https://your_app_url.com/webhook

{
  "webhook_type": "AUTH",
  "webhook_code": "AUTOMATICALLY_VERIFIED",
  "item_id": "zeWoWyv84xfkGg1w4ox5iQy5k6j75xu8QXMEm",
  "account_id": "vzeNDwK7KQIm4yEog683uElbp9GRLEFXGK98D",
  "environment": "production"
}
`}</code></pre>
    <p>{`Occasionally automatic verification may fail, likely due to erroneous user input, such as an incorrect
account and routing number pair. If the Item is unable to be verified within seven days, Plaid will send
a `}<a parentName="p" {...{
        "href": "/docs/api/products/auth/#verification_expired"
      }}><inlineCode parentName="a">{`VERIFICATION_EXPIRED`}</inlineCode></a>{`
webhook. When verification fails, the Item is permanently locked; we recommend prompting your user to
retry connecting their institution via Link.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "#Auth VERIFICATION_EXPIRED webhook",
        "#Auth": true,
        "VERIFICATION_EXPIRED": true,
        "webhook": true
      }}>{`> POST https://your_app_url.com/webhook

{
  "webhook_type": "AUTH",
  "webhook_code": "VERIFICATION_EXPIRED",
  "item_id": "zeWoWyv84xfkGg1w4ox5iQy5k6j75xu8QXMEm",
  "account_id": "vzeNDwK7KQIm4yEog683uElbp9GRLEFXGK98D",
  "environment": "production"
}
`}</code></pre>
    <Callout mdxType="Callout">
      <p>{`If Plaid encounters an `}<inlineCode parentName="p">{`ITEM_LOGIN_REQUIRED`}</inlineCode>{` error during attempted validation, this may mean that Plaid lost access to the user's account after sending this micro-deposit but before being able to verify it. If this occurs, send the user through the `}<a parentName="p" {...{
          "href": "/docs/link/update-mode"
        }}>{`update mode`}</a>{` flow to re-verify their account.`}</p>
    </Callout>
    <p>{`The example code below shows how to handle `}<inlineCode parentName="p">{`AUTOMATICALLY_VERIFIED`}</inlineCode>{` and `}<inlineCode parentName="p">{`VERIFICATION_EXPIRED`}</inlineCode>{` webhooks
and call `}<inlineCode parentName="p">{`/auth/get`}</inlineCode>{` to retrieve account and routing data.`}</p>
    <blockquote>
      <p parentName="blockquote">{`If you are using the Sandbox environment, you can use
the `}<inlineCode parentName="p">{`/sandbox/item/set_verification_status`}</inlineCode>{`
endpoint to test your integration.`}</p>
    </blockquote>
    <MDXCodeBlock type='server' title='Webhook example code' mdxType="MDXCodeBlock">
      <pre><code parentName="pre" {...{
          "className": "language-bash"
        }}>{`## no curl example
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-node"
        }}>{`// This example uses Express to receive webhooks
const app = require('express')();
const bodyParser = require('body-parser');
app.use(bodyParser);

app.post('/webhook', async (request, response) => {
  const event = request.body;

  // Handle the event
  switch (event.webhook_code) {
    case 'AUTOMATICALLY_VERIFIED':
      const accessToken = lookupAccessToken(event.item_id);
      const request: AuthGetRequest = { access_token: accessToken };
      const authResponse = await client.authGet(request);
      const numbers = authResponse.data.numbers;
      break;
    case 'VERIFICATION_EXPIRED':
      // handle verification failure; prompt user to re-authenticate
      console.error('Verification failed for', event.item_id);
      break;
    default:
      // Unexpected event type
      return response.status(400).end();
  }

  // Return a response to acknowledge receipt of the event
  response.json({ received: true });
});

app.listen(8000, () => console.log('Running on port 8000'));
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-python"
        }}>{`# This example uses Django to receive webhooks
import json
from django.http import HttpResponse

@csrf_exempt
def webhook_view(request):
  event = json.loads(request.body)

  # Handle the event
  if event['webhook_code'] == 'AUTOMATICALLY_VERIFIED':
    access_token = lookup_access_token(event['item_id']);
    request = AuthGetRequest(access_token=access_token)
    auth_response = client.auth_get(request)
    numbers = auth_response['numbers']
  elif event['webhook_code'] == 'VERIFICATION_EXPIRED':
    print('Verification failed for ', event['item_id'])
    # handle verification failure; prompt user to re-authenticate
  else:
    # Unexpected event type
    return HttpResponse(status=400)

  return HttpResponse(status=200)
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-ruby"
        }}>{`# Using Sinatra
require 'json'

post '/webhook' do
  event = JSON.parse(request.body.read)

  # Handle the event
  case event['webhook_code']
  when 'AUTOMATICALLY_VERIFIED'
    access_token = lookup_access_token(event['item_id']);
    request = Plaid::AuthGetRequest.new({ access_token: access_token })
    response = client.auth_get(request)
    numbers = response.numbers
  when 'VERIFICATION_EXPIRED'
    puts "Verification failed for #{event['item_id']}"
    # Handle verification failure; prompt user to re-authenticate
  else
    # Unexpected event type
    status 400
    return
  end

  status 200
end
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-java"
        }}>{`// No example implementation for Java
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go"
        }}>{`// No example implementation for Go
`}</code></pre>
    </MDXCodeBlock>
    <h4 {...{
      "id": "check-the-account-verification-status-optional"
    }}>{`Check the account verification status `}<em parentName="h4">{`(optional)`}</em></h4>
    <p>{`In some cases you may want to implement logic to display the `}<inlineCode parentName="p">{`verification_status`}</inlineCode>{` of an Item
that is pending automated verification in your app. The `}<inlineCode parentName="p">{`/accounts/get`}</inlineCode>{`
API endpoint allows you to query this information.`}</p>
    <p>{`For non-programmatic access to this information, you can use the `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/account-verification/"
      }}>{`Account Verification Dashboard`}</a>{`.`}</p>
    <MDXCodeBlock type='server' title='Accounts get request' mdxType="MDXCodeBlock">
      <pre><code parentName="pre" {...{
          "className": "language-bash"
        }}>{`curl -X POST https://sandbox.plaid.com/accounts/get \\
  -H 'Content-Type: application/json' \\
  -d '{
    "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
    "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
    "access_token": "access-sandbox-5cd6e1b1-1b5b-459d-9284-366e2da89755"
  }'
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-node"
        }}>{`// Fetch the accountID and accessToken from your database
const accountID = 'vzeNDwK7KQIm4yEog683uElbp9GRLEFXGK98D';
const accessToken = 'access-sandbox-5cd6e1b1-1b5b-459d-9284-366e2da89755';
const request: AccountsGetRequest = {
  access_token: accessToken,
};
try {
  const response = await client.accountsGet(request);
  const account = response.data.accounts.find((a) => a.account_id === accountID);
  const verificationStatus = account.verification_status;
} catch (err) {
  // handle error
}
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-python"
        }}>{`# Fetch the accountID and accessToken from your database
account_id = 'vzeNDwK7KQIm4yEog683uElbp9GRLEFXGK98D'
access_token = 'access-sandbox-5cd6e1b1-1b5b-459d-9284-366e2da89755'

request = AccountsGetRequest(access_token=access_token)
response = client.accounts_get(request)
accounts = response['accounts']
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-ruby"
        }}>{`# Fetch the accountID and accessToken from your database
account_id = 'vzeNDwK7KQIm4yEog683uElbp9GRLEFXGK98D'
access_token = 'access-sandbox-5cd6e1b1-1b5b-459d-9284-366e2da89755'

request = Plaid::AccountsGetRequest.new({ access_token: access_token })
response = client.accounts_get(request)
accounts = response.accounts
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-java"
        }}>{`// Fetch the accountID and accessToken from your database
String accountID = "vzeNDwK7KQIm4yEog683uElbp9GRLEFXGK98D";
String accessToken = "access-sandbox-5cd6e1b1-1b5b-459d-9284-366e2da89755";

AccountsGetRequest request = new AccountsGetRequest()
  .accessToken(accessToken);

Response<AccountsGetResponse> response = client()
  .accountsGet(request)
  .execute();

if (response.isSuccessful()) {
  AccountBase account = response.body().getAccounts().stream()
    .filter(a -> a.getAccountId().equals(accountID))
    .findFirst()
    .orElseThrow();
  String verificationStatus = account.getVerificationStatus().getValue();
}
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go"
        }}>{`// Fetch the accountID and accessToken from your database
accountID := "vzeNDwK7KQIm4yEog683uElbp9GRLEFXGK98D"
accessToken := "access-sandbox-5cd6e1b1-1b5b-459d-9284-366e2da89755"

accountsGetResp, _, err := client.PlaidApi.AccountsGet(ctx).AccountsGetRequest(
  *plaid.NewAccountsGetRequest(accessToken),
).Execute()
accounts := accountsGetResp.GetAccounts()
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go",
          "metastring": "sdk=fern",
          "sdk": "fern"
        }}>{`// Fetch the accountID and accessToken from your database
accountID := "vzeNDwK7KQIm4yEog683uElbp9GRLEFXGK98D"
accessToken := "access-sandbox-5cd6e1b1-1b5b-459d-9284-366e2da89755"

accountsGetResp, err := client.Accounts.Get(ctx, &plaid.AccountsGetRequest{AccessToken: accessToken})
accounts := accountsGetResp.GetAccounts()
`}</code></pre>
    </MDXCodeBlock>
    
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "#Account get response",
        "#Account": true,
        "get": true,
        "response": true
      }}>{`{
  "accounts": [
    {
      "account_id": "vzeNDwK7KQIm4yEog683uElbp9GRLEFXGK98D",
      "balances": { Object },
      "mask": "0000",
      "name": "Plaid Checking",
      "official_name": "Plaid Gold Checking",
      "type": "depository",
      "subtype": "checking",
      "verification_status":
        "pending_automatic_verification" |
        "automatically_verified" |
        "verification_expired"
    },
    ...
  ],
  "item": { Object },
  "request_id": String
}
`}</code></pre>
    <h4 {...{
      "id": "fetch-auth-data"
    }}>{`Fetch Auth data`}</h4>
    <p>{`Finally, we can retrieve Auth data once automated verification has succeeded:`}</p>
    <MDXCodeBlock type='server' title='Auth request' mdxType="MDXCodeBlock">
      <pre><code parentName="pre" {...{
          "className": "language-bash"
        }}>{`curl -X POST https://sandbox.plaid.com/auth/get \\
  -H 'Content-Type: application/json' \\
  -d '{
    "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
    "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
    "access_token": "access-sandbox-5cd6e1b1-1b5b-459d-9284-366e2da89755"
  }'
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-node"
        }}>{`const accessToken = 'access-sandbox-5cd6e1b1-1b5b-459d-9284-366e2da89755';

// Instantly fetch Auth numbers
const request: AuthGetRequest = {
  access_token: accessToken,
};
try {
  const response = await client.authGet(request);
  const numbers = response.data.numbers;
} catch (err) {
  // handle error
}
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-python"
        }}>{`access_token = 'access-sandbox-5cd6e1b1-1b5b-459d-9284-366e2da89755'

# Instantly fetch Auth numbers
auth_request = AuthGetRequest(access_token=access_token)
auth_response = client.auth_get(auth_request)
numbers = auth_response['numbers']
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-ruby"
        }}>{`access_token = 'access-sandbox-5cd6e1b1-1b5b-459d-9284-366e2da89755';

# Instantly fetch Auth numbers
request = Plaid::AuthGetRequest.new({ access_token: access_token })
auth_response = client.auth_get(request)
numbers = auth_response.numbers
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-java"
        }}>{`String accessToken = "access-sandbox-5cd6e1b1-1b5b-459d-9284-366e2da89755";

// Instantly fetch Auth numbers
AuthGetRequest authGetRequest = new AuthGetRequest()
  .accessToken(accessToken);

Response<AuthGetResponse> authResponse = client()
  .authGet(authGetRequest)
  .execute();

if (authResponse.isSuccessful()) {
  AuthGetNumbers numbers = authResponse.body().getNumbers();
}
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go"
        }}>{`accessToken := "access-sandbox-5cd6e1b1-1b5b-459d-9284-366e2da89755"

// Instantly fetch Auth numbers
authGetResp, _, err := client.PlaidApi.AuthGet(ctx).AuthGetRequest(
  *plaid.NewAuthGetRequest(accessToken),
).Execute()

numbers := authGetResp.GetNumbers()
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go",
          "metastring": "sdk=fern",
          "sdk": "fern"
        }}>{`accessToken := "access-sandbox-5cd6e1b1-1b5b-459d-9284-366e2da89755"

// Instantly fetch Auth numbers
authGetResp, err := client.Auth.Get(ctx, &plaid.AuthGetRequest{AccessToken: accessToken})

numbers := authGetResp.GetNumbers()
`}</code></pre>
    </MDXCodeBlock>
    
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "#Auth response",
        "#Auth": true,
        "response": true
      }}>{`{
  "numbers": {
    "ach": [
      {
        "account_id": "vzeNDwK7KQIm4yEog683uElbp9GRLEFXGK98D",
        "account": "1111222233330000",
        "routing": "011401533",
        "wire_routing": "021000021"
      }
    ],
    "eft": [],
    "international": [],
    "bacs": []
  },
  "accounts": [
    {
      "account_id": "vzeNDwK7KQIm4yEog683uElbp9GRLEFXGK98D",
      "balances": { Object },
      "mask": "0000",
      "name": "Plaid Checking",
      "official_name": "Plaid Gold Standard 0% Interest Checking",
      "verification_status": "automatically_verified",
      "subtype": "checking" | "savings",
      "type": "depository"
    }
  ],
  "item": { Object },
  "request_id": "m8MDnv9okwxFNBV"
}
`}</code></pre>
    <p>{`Check out the `}<inlineCode parentName="p">{`/auth/get`}</inlineCode>{` API reference documentation to see the full
Auth request and response schema.`}</p>
    <h4 {...{
      "id": "handling-link-events"
    }}>{`Handling Link events`}</h4>
    <p>{`For a user who goes through the Automated Micro-deposit flow, the `}<inlineCode parentName="p">{`TRANSITION_VIEW (view_name = NUMBERS)`}</inlineCode>{`
event will occur after `}<inlineCode parentName="p">{`SUBMIT_CREDENTIALS`}</inlineCode>{`, and in the `}<inlineCode parentName="p">{`onSuccess`}</inlineCode>{` callback the
`}<inlineCode parentName="p">{`verification_status`}</inlineCode>{` will be `}<inlineCode parentName="p">{`pending_automatic_verification`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash",
        "metastring": "switcher=false #Sample Link events for Automated Micro-deposits flow",
        "switcher": "false",
        "#Sample": true,
        "Link": true,
        "events": true,
        "for": true,
        "Automated": true,
        "Micro-deposits": true,
        "flow": true
      }}>{`OPEN (view_name = CONSENT)
TRANSITION_VIEW (view_name = SELECT_INSTITUTION)
SEARCH_INSTITUTION
SELECT_INSTITUTION
TRANSITION_VIEW (view_name = CREDENTIAL)
SUBMIT_CREDENTIALS
TRANSITION_VIEW (view_name = LOADING)
TRANSITION_VIEW (view_name = MFA, mfa_type = code)
SUBMIT_MFA (mfa_type = code)
TRANSITION_VIEW (view_name = LOADING)
TRANSITION_VIEW (view_name = SELECT_ACCOUNT)
TRANSITION_VIEW (view_name = NUMBERS)
TRANSITION_VIEW (view_name = LOADING)
TRANSITION_VIEW (view_name = CONNECTED)
HANDOFF
onSuccess (verification_status: pending_automatic_verification)
`}</code></pre>
    <Actions.Wrapper>
  <Actions.Card link='/docs/auth/coverage/same-day' linkText='View guide' title='Same-Day Micro-deposits' subtitle='Integrate the manual micro-deposit method' icon={<Auth mdxType="Auth" />} />
  <Actions.Card link='/docs/auth/coverage/testing' linkText='View guide' title='Testing in Sandbox' subtitle='Learn how to test each Auth method in the Sandbox' icon={<Auth mdxType="Auth" />} />
    </Actions.Wrapper>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "the-automated-micro-deposit-flow",
        level: 4,
        title: "The Automated Micro-deposit flow",
      },
{
        id: "configure--create-a-link_token",
        level: 4,
        title: "Configure & Create a link_token",
      },
{
        id: "initialize-link-with-a-link_token",
        level: 4,
        title: "Initialize Link with a link_token",
      },
{
        id: "exchange-the-public-token",
        level: 4,
        title: "Exchange the public token",
      },
{
        id: "handle-auth-webhooks",
        level: 4,
        title: "Handle Auth webhooks",
      },
{
        id: "check-the-account-verification-status-optional",
        level: 4,
        title: <React.Fragment>{`Check the account verification status `}<em>{`(optional)`}</em></React.Fragment>,
      },
{
        id: "fetch-auth-data",
        level: 4,
        title: "Fetch Auth data",
      },
{
        id: "handling-link-events",
        level: 4,
        title: "Handling Link events",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
